import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal-dialog focus behaviour: move focus in on open, trap Tab/Shift+Tab
 * inside, optionally close on Escape, and restore focus to whatever was
 * focused before on close.
 *
 * Returns a ref to attach to the dialog element (which needs `tabIndex={-1}`
 * so it can hold focus itself when nothing inside it does).
 *
 * `onEscape` is optional: pass a falsy value for states where Escape has no
 * safe meaning, and no Escape binding exists at all for that render.
 */
export default function useDialogFocus({ active, onEscape }) {
  const dialogRef = useRef(null);
  // Read the Escape handler through a ref so callers passing a fresh closure
  // each render — or flipping Escape off for a particular state — never
  // re-run the effect. Re-running would restore focus out of a dialog that
  // never actually closed.
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;

    // Restore focus to whatever triggered the dialog once it closes.
    const previouslyFocused = document.activeElement;

    const getFocusable = () => {
      const dialog = dialogRef.current;
      if (!dialog) return [];
      return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
    };

    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (escapeRef.current) {
          e.preventDefault();
          escapeRef.current();
        }
        return;
      }
      if (e.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      // Trap focus within the dialog so Tab/Shift+Tab never walks the page
      // behind the overlay — those controls are visually covered, so
      // activating one is always an accident.
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (!dialog.contains(current)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (current === first || current === dialog)) {
        // The dialog panel itself sits before everything inside it, so
        // Shift+Tab off it would otherwise land on the page behind.
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    // Move focus into the dialog. Wait a frame so a child's own autoFocus
    // (e.g. a modal's code input) has already landed — don't steal it back.
    // Only fall back to the dialog panel itself if nothing inside grabbed
    // focus on its own.
    const raf = requestAnimationFrame(() => {
      if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) {
        dialogRef.current.focus();
      }
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
      if (previouslyFocused instanceof HTMLElement && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active]);

  return dialogRef;
}
